import { useQuery } from '@tanstack/react-query';
import { Form, Input, Skeleton, Switch } from 'antd';

import { apiFetch } from '@/api/client';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { handleEnterKeyNavigation, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, PageHeading } from '@/components/PageHeading';
import { StatusBadge, type StatusTone } from '@/components/StatusBadge';
import { formatDateTime } from '@core/format/dates';
import { blankToNull } from '@core/format/text';
import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';

type TenantStatus = 'Trial' | 'Active' | 'PastDue' | 'Suspended' | 'Cancelled';

interface TenantProfile {
  id: number;
  code: string;
  name: string;
  legalName: string | null;
  gstin: string | null;
  pan: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: TenantStatus;
  supportAccessConsent: boolean;
  wentLiveOn: string | null;
}

interface ProfileFormValues {
  name: string;
  legalName?: string | null;
  gstin?: string | null;
  pan?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  supportAccessConsent: boolean;
}

const profileKey = ['tenant'] as const;

const statusTone: Record<TenantStatus, StatusTone> = {
  Trial: 'brand',
  Active: 'positive',
  PastDue: 'warning',
  Suspended: 'negative',
  Cancelled: 'neutral',
};

/**
 * The company's own profile (spec Sections 11 and 23).
 *
 * There is no company id anywhere in this screen or the API behind it: the company acted on is the
 * one in the caller's token, so an administrator can only ever reach their own. Code and status
 * are shown but not editable — both are set by us when the account is provisioned.
 *
 * The support-access switch is the one that matters. It is set here, by the customer, and nowhere
 * else: a platform-side toggle would be support granting itself permission to sign in as this
 * company's users, which is not consent. It is therefore worded as what it actually permits rather
 * than as a preference.
 */
export function CompanyProfilePage({ session }: { session: Session }) {
  const [form] = Form.useForm<ProfileFormValues>();

  const profile = useQuery({
    queryKey: profileKey,
    queryFn: () => apiFetch<TenantProfile>('/api/tenant'),
  });

  const editable = canAct(session, 'Tenant:Edit');
  const fields = useAutoFocusFirstField<HTMLDivElement>(editable);

  const save = useApiMutation<TenantProfile, ProfileFormValues>({
    mutationFn: (values) =>
      apiFetch<TenantProfile>('/api/tenant', { method: 'PUT', body: blankToNull(values) }),
    success: 'Company profile saved.',
    invalidates: [profileKey],
  });

  if (profile.isError) {
    return <ApiErrorAlert error={profile.error} />;
  }

  return (
    <>
      <PageHeading
        title="Company profile"
        subtitle="Your own details, as they appear on documents you issue."
      />

      <Card className="mb-4">
        {profile.isLoading || !profile.data ? (
          <Skeleton active paragraph={{ rows: 1 }} />
        ) : (
          // A definition grid rather than antd's `Descriptions`: four read-only facts do not need a
          // component with column spans and a bordered mode, and this one wears the template's own
          // label treatment without a second set of tokens to keep in step.
          <dl className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Fact label="Company code">
              <span className="font-mono-nums font-semibold text-gray-900">
                {profile.data.code}
              </span>
            </Fact>

            <Fact label="Status">
              <StatusBadge tone={statusTone[profile.data.status]}>
                {profile.data.status}
              </StatusBadge>
            </Fact>

            <Fact label="Plan">{session.user.entitlements.planCode ?? '—'}</Fact>

            <Fact label="Live since">{formatDateTime(profile.data.wentLiveOn)}</Fact>
          </dl>
        )}
      </Card>

      {profile.data && (
        <Card>
          <Form<ProfileFormValues>
            form={form}
            layout="vertical"
            disabled={!editable}
            initialValues={profile.data}
            requiredMark={false}
            onFinish={(values) => void submitVia(save, values)}
            // The one data-entry form on this screen, so it gets the same Enter-to-next-field
            // behaviour the drawers have — see `enterKeyNavigation`.
            onKeyDown={handleEnterKeyNavigation}
          >
            {/* `editable` gates it too: on a read-only view every field is disabled, so there is
                nothing to focus and nothing this could usefully do. */}
            <div ref={fields} className="grid gap-x-4 md:grid-cols-2">
              <Form.Item
                name="name"
                label="Trading name"
                rules={[{ required: true, message: 'A name is required' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="legalName"
                label="Legal name"
                extra="As registered. This is what appears on a tax invoice."
              >
                <Input />
              </Form.Item>
            </div>

            <div className="grid gap-x-4 md:grid-cols-2">
              <Form.Item
                name="gstin"
                label="GSTIN"
                extra="The company-level registration. Each branch carries its own, since GST is registered per state."
                rules={[{ len: 15, message: 'A GSTIN is 15 characters' }]}
              >
                <Input maxLength={15} className="font-mono-nums" />
              </Form.Item>

              <Form.Item
                name="pan"
                label="PAN"
                rules={[
                  {
                    pattern: /^[A-Za-z]{5}\d{4}[A-Za-z]$/,
                    message: 'A PAN is five letters, four digits and a letter',
                  },
                ]}
              >
                <Input maxLength={10} className="font-mono-nums" />
              </Form.Item>
            </div>

            <div className="grid gap-x-4 md:grid-cols-2">
              <Form.Item
                name="contactEmail"
                label="Contact email"
                rules={[{ type: 'email', message: 'Not a valid email address' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item name="contactPhone" label="Contact phone">
                <Input className="font-mono-nums" />
              </Form.Item>
            </div>

            <div className="mt-2 border-t border-border-color pt-4">
              <h2 className="text-sm font-bold text-gray-900">Support access</h2>

              {/*
                Worded as what it permits, not as a preference, and kept as a notice rather than a
                tooltip — this is the one setting on the screen that lets someone outside the
                company see the company's data, and it should cost a paragraph to turn on.
              */}
              <div className="mt-2 mb-3 rounded-md border border-border-color bg-light p-3">
                <p className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                  <i className="icon icon-info text-info" />
                  What this permits
                </p>
                <p className="mt-1 text-[13px] text-default">
                  With it on, our support staff can sign in as one of your users to see what you see
                  — every action recorded against the real person and marked as impersonated. With
                  it off, they cannot, and nobody here can turn it on for you.
                </p>
              </div>

              <Form.Item
                name="supportAccessConsent"
                label="Allow support to sign in as one of our users"
                valuePropName="checked"
              >
                <Switch size="small" />
              </Form.Item>
            </div>

            {editable && (
              <div className="flex justify-end border-t border-border-color pt-4">
                <Button
                  variant="primary"
                  type="submit"
                  loading={save.isPending}
                  disabled={save.isPending}
                >
                  {save.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            )}
          </Form>
        </Card>
      )}
    </>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] tracking-wide text-default uppercase">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );
}
